import { useState, useEffect, useContext } from "react";
import { Container, Grid } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ItineraryContext } from "../../../../constants/layouts/itinerary-layout";
import { LocaleContext } from "../../../../routing/LangRouter";
import moment from "moment";
import { connect } from "react-redux";
import BASE_URL from "../../../../constants/baseUrls";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { BREAKPOINT, WIDTH } from "../../../../constants/breakpoints";
import { saveTicketSelectedFilters } from "../../../../store/actions";

//import general components
import RecommendDivider from "../../../common/recommend-divider/recommend-divider";

//import view components
import FlightCard from "../../../common/minimal-flight-card/minimal-flight-card";

//import styles
import "./recommended-flight.scss";

function RecommendedFlight({ dispatch, flightList }) {
    //general variables
    const navigate = useNavigate();
    const [_, setSearchParams] = useSearchParams();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const [parent] = useAutoAnimate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const favorites = JSON.parse(sessionStorage.getItem("favorites"));
    const isTwoWay = domesticFlightInformation?.returnDate;

    //request user favorites
    const getUserFavorites = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/account/user/favorites/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            // Filter out null values
            const favoritesData = response.data;
            const filteredFavorites = Object.fromEntries(Object.entries(favoritesData).filter(([favoriteKey, value]) => value !== null));
            sessionStorage.setItem("favorites", JSON.stringify(filteredFavorites));
        } catch (error) {
            console.error("Error fetching user favorites:", error);
        }
    };

    useEffect(() => {
        if (!favorites && accessToken) getUserFavorites();
    }, [favorites, accessToken]);

    //handle calculate points for a flight item
    const [suggestedFlightList, setSuggestedFlightList] = useState([]);
    useEffect(() => {
        if (!flightList?.length || !favorites) {
            setSuggestedFlightList([]);
            return;
        }

        const calculatePoints = flightItem => {
            const { cobin, departure_time } = flightItem;
            const { price_class, sleep_class } = favorites;

            const getCobinPoints = () => {
                if (cobin === "ECONOMY") {
                    return price_class === 1 ? 5 : price_class === 2 ? 4 : 0;
                }
                return price_class === 3 ? 5 : price_class === 2 ? 1 : 0;
            };

            const getTimePoints = () => {
                const departureTime = parseInt(departure_time);
                if (departureTime >= 5 && departureTime <= 10) {
                    return sleep_class === 1 ? 3 : sleep_class === 2 ? 2 : 1;
                }
                if (departureTime >= 11 && departureTime <= 19) {
                    return sleep_class === 2 ? 3 : sleep_class === 1 ? 2 : 3;
                }
                if ((departureTime >= 20 && departureTime <= 23) || (departureTime >= 0 && departureTime <= 4)) {
                    return sleep_class === 3 ? 2 : 1;
                }
            };

            return getCobinPoints() + getTimePoints();
        };

        const flightListCopy = flightList.map(flightItem => ({
            ...flightItem,
            point: calculatePoints(flightItem),
        }));

        const sortedFlightList = flightListCopy.sort((a, b) => b.point - a.point);
        setSuggestedFlightList(sortedFlightList);
    }, [flightList]);

    //Checks if the flight capacity is sufficient for the number of passengers.
    const checkFlightCapacity = capacity => {
        const totalPersons = domesticFlightInformation.personCounter.adult + domesticFlightInformation.personCounter.child;
        if (capacity < totalPersons) {
            toast.error(t("flightMessage1"), { toastId: "custom-id-yes" });
            return false;
        } else return true;
    };

    //Updates the itinerary generation status and saves the updated itinerary information to sessionStorage.
    const updateItineraryGenerate = () => {
        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
    };

    //Updates the itinerary information with a new date and time by adding a specified number of hours.
    const updateItineraryDateTime = (type, date, time, hours) => {
        const fullDate = moment(`${date} ${time.split(":", 1).join(":00")}`)
            .add(hours, "hours")
            .format()
            .split("+")[0];

        const [newDate, newTime] = fullDate.split("T");
        if (type === "departure") {
            itineraryInformation.departureDate = newDate;
            itineraryInformation.firstDayTime = newTime;
        } else if (type === "return") {
            itineraryInformation.returnDate = newDate;
            itineraryInformation.lastDayTime = newTime;
        }
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    //Navigates to the next page based on the accommodation status and updates the routes and session storage accordingly.
    const navigateNextPage = () => {
        const isAccommodation = generalInformation?.isAccommodation;
        const nextRoute = `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`;
        const updatedRoutes = [...routes, nextRoute];
        setRoutes(updatedRoutes);
        sessionStorage.setItem("visitedRoutes", JSON.stringify(updatedRoutes));
        navigate(nextRoute);
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //Handles the selection of a ticket and performs necessary actions based on the ticket type and conditions.
    const selectTicket = ticket => {
        if (!checkFlightCapacity(ticket.capacity)) return;
        updateItineraryGenerate();
        resetFilters();

        sessionStorage.setItem("selectedFlight", JSON.stringify(ticket));
        if (isItinerary) {
            updateItineraryDateTime("departure", ticket?.departure_date, ticket?.departure_time, 5);
            if (!!isTwoWay) {
                updateItineraryDateTime("return", ticket?.departure_date, ticket?.departure_time, -3);
            }
            navigateNextPage();
        } else navigate(`/${locale}/flight-passengers`);
    };

    //Handles the selection of a ticket based on the screen width.
    //If the screen width is less than the breakpoint, it sets the search parameters an
    const handleSelectedTicket = ticket => {
        if (WIDTH < BREAKPOINT) {
            setSearchParams({ action: "flight-details" });
            sessionStorage.setItem("flightDetails", JSON.stringify(ticket));
        } else {
            selectTicket(ticket);
        }
    };

    // Handle dropdown expansion for mobile devices
    const [isExpanded, setIsExpanded] = useState(WIDTH > BREAKPOINT);
    const toggleExpansion = () => {
        if (WIDTH < BREAKPOINT) {
            setIsExpanded(prevState => !prevState);
        }
    };

    return (
        <>
            {suggestedFlightList?.length > 0 && (
                <Grid className="recommended-flight" container item xs={12} rowGap={{ xs: 0, md: 4 }} alignItems="flex-start" ref={parent}>
                    <RecommendDivider title={t("atripaSuggestion")} handleClick={toggleExpansion} isExpanded={isExpanded}>
                        {isExpanded && (
                            <Container maxWidth="xl">
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    rowGap={3}
                                    columnGap={{ xs: 1, xl: 6 }}
                                    mt={{ xs: 3, md: 0 }}
                                    justifyContent={{ xs: "space-between", lg: "flex-start" }}
                                >
                                    {suggestedFlightList?.slice(0, 3).map((suggestedFlightItem, index) => {
                                        return (
                                            <Grid
                                                className="recommended-flight__card"
                                                key={index}
                                                container
                                                item
                                                xs={12}
                                                md={3.9}
                                                xl={3.7}
                                                rowGap={2}
                                            >
                                                <span className="recommended-flight__index">{index + 1}</span>
                                                <FlightCard flightItem={suggestedFlightItem} isClickable={true} handleClick={handleSelectedTicket} type="suggest" />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Container>
                        )}
                    </RecommendDivider>
                </Grid>
            )}
        </>
    );
}

export default connect()(RecommendedFlight);

//Checks the zamzam flight selling type compatibility between departure and return flights.
// const checkSellingType = (departureSelling, returnSelling) => {
//     if (!returnSelling) return true;
//     if (departureSelling === "All" || returnSelling === "All") return true;
//     if (departureSelling === returnSelling) return true;
//     const incompatibleSellingTypes = {
//         "Only Iranian": {
//             "Non Iranian": {
//                 en: "Your selected flight is only for non-Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران غیرایرانی است.",
//             },
//         },
//         "Non Iranian": {
//             "Only Iranian": {
//                 en: "Your selected flight is only for Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران ایرانی است.",
//             },
//         },
//     };
//     if (incompatibleSellingTypes[departureSelling] && incompatibleSellingTypes[departureSelling][returnSelling]) {
//         const errorMessage =
//             locale === "en"
//                 ? incompatibleSellingTypes[departureSelling][returnSelling].en
//                 : incompatibleSellingTypes[departureSelling][returnSelling].fa;
//         toast.error(errorMessage, { toastId: "custom-id-yes" });
//         return false;
//     }
//     return true;
// };
